/*
 * qlearn_account_created: one dataLayer event when the server confirms a
 * brand-new account, for the ads sign-up conversion (ads attribution
 * contract, 2026-10-20).
 *
 * "Brand-new" is the server's word, never the browser's. Google sign-in
 * matches-or-creates, so the browser cannot tell a new account from a
 * returning one by itself. createdAt on /user/me (wire contract in
 * activation-timing.js) must parse and fall between 2 minutes ahead of now
 * (device clock skew) and 15 minutes behind it. An absent or unparseable
 * createdAt emits nothing; a stand-in time would be a conversion that never
 * happened.
 *
 * Every sign-in door looks the student up through the user saga, so the push
 * lives there. The payload is the event and the door when the sign-in that
 * started the lookup named one; nothing from the account rides along.
 *
 * One event per account per device, latched in localStorage by account (as
 * activation-timing.js latches) and only after the push. Sign-out's
 * localStorage.clear() drops the latch, so signing in again within the
 * 15 minutes reports again. Never throws: it sits on the sign-in path.
 */
export const ACCOUNT_CREATED_EVENT = "qlearn_account_created";

const DOORS = ["google", "phone", "email_code", "institutional", "apple"];
const NEW_ACCOUNT_MAX_AGE_MS = 15 * 60 * 1000;
const CLOCK_SKEW_MS = 2 * 60 * 1000;

const sentKeyFor = (accountId) => `qlearn_account_created_sent:${accountId}`;

export const trackAccountCreated = (user, options = {}, nowMs = Date.now()) => {
  try {
    const door = options?.door;
    const accountId = user?.id;
    const createdMs = typeof user?.createdAt === "string" ? Date.parse(user.createdAt) : NaN;
    if (!accountId || !Number.isFinite(createdMs)) return false;
    const ageMs = nowMs - createdMs;
    if (ageMs < -CLOCK_SKEW_MS || ageMs > NEW_ACCOUNT_MAX_AGE_MS) return false;

    const sentKey = sentKeyFor(accountId);
    if (window.localStorage.getItem(sentKey)) return false;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: ACCOUNT_CREATED_EVENT,
      ...(DOORS.includes(door) ? { door } : {}),
    });
    window.localStorage.setItem(sentKey, "1");
    return true;
  } catch (e) {
    return false; // storage or the dataLayer unavailable: the event is lost, the sign-in is not
  }
};
